import type { DodCriterion } from './types';

let _seq = 0;
/** A fresh blank criterion. `keyHint` only varies the id; ids are client-local. */
export function newCriterion(keyHint: string): DodCriterion {
  _seq += 1;
  return { id: `dod-${keyHint}-${_seq}`, text: '', met: false, position: 0 };
}

/** Sort by position, then re-pack positions to a dense 0..n-1. */
export function normalize(items: ReadonlyArray<DodCriterion>): DodCriterion[] {
  return [...items]
    .sort((a, b) => a.position - b.position)
    .map((c, i) => ({ ...c, position: i }));
}

export function addCriterion(
  items: ReadonlyArray<DodCriterion>,
  criterion: DodCriterion
): DodCriterion[] {
  return normalize([...items, criterion]);
}

export function removeCriterion(items: ReadonlyArray<DodCriterion>, id: string): DodCriterion[] {
  return normalize(items.filter((c) => c.id !== id));
}

export function moveCriterion(
  items: ReadonlyArray<DodCriterion>,
  id: string,
  dir: 'up' | 'down'
): DodCriterion[] {
  const ordered = normalize(items);
  const idx = ordered.findIndex((c) => c.id === id);
  if (idx < 0) return ordered;
  const swap = dir === 'up' ? idx - 1 : idx + 1;
  if (swap < 0 || swap >= ordered.length) return ordered;
  const next = [...ordered];
  [next[idx], next[swap]] = [next[swap], next[idx]];
  // Re-assign positions by array index so normalize's sort preserves the new order.
  return next.map((c, i) => ({ ...c, position: i }));
}

/** Apply a new top-to-bottom order (by id) and re-pack positions. Ids not in
 *  `orderedIds` are appended in their existing order (defensive). */
export function reorderCriteria(
  items: ReadonlyArray<DodCriterion>,
  orderedIds: ReadonlyArray<string>
): DodCriterion[] {
  const byId = new Map(items.map((c) => [c.id, c] as const));
  const seen = new Set(orderedIds);
  const ordered = orderedIds
    .map((id) => byId.get(id))
    .filter((c): c is DodCriterion => Boolean(c));
  const rest = items.filter((c) => !seen.has(c.id));
  return [...ordered, ...rest].map((c, i) => ({ ...c, position: i }));
}

export function toggleMet(items: ReadonlyArray<DodCriterion>, id: string): DodCriterion[] {
  return items.map((c) => (c.id === id ? { ...c, met: !c.met } : c));
}

export function setText(items: ReadonlyArray<DodCriterion>, id: string, text: string): DodCriterion[] {
  return items.map((c) => (c.id === id ? { ...c, text } : c));
}

/** Content equality (ignores array identity) — drives the sheet's dirty check. */
export function dodEqual(a: ReadonlyArray<DodCriterion>, b: ReadonlyArray<DodCriterion>): boolean {
  const na = normalize(a);
  const nb = normalize(b);
  if (na.length !== nb.length) return false;
  return na.every(
    (c, i) =>
      c.id === nb[i].id &&
      c.text === nb[i].text &&
      c.met === nb[i].met &&
      c.position === nb[i].position
  );
}
